import { useCallback, useEffect, useState } from 'react';
import { onForegroundMessage, isFirebaseConfigured } from '@/utils/firebase';

export type PushCardKind = 'ok' | 'bad' | 'info';

export interface PushCardItem {
  id: string;
  title: string;
  body: string;
  kind?: PushCardKind;
  at: number;
}

let seq = 0;
const nextId = () => `push-${Date.now()}-${seq++}`;

/**
 * Foreground push cards for ride-day pages. Subscribes to Firebase foreground messages whose
 * `data.module === 'rides'` (optionally filtered to one booking/availability), and lets the page
 * push its own cards for status changes noticed while polling.
 */
export function usePushCards(filter?: { bookingId?: number; availabilityId?: number }) {
  const [items, setItems] = useState<PushCardItem[]>([]);

  const push = useCallback((title: string, body: string, kind: PushCardKind = 'info') => {
    setItems((prev) => [{ id: nextId(), title, body, kind, at: Date.now() }, ...prev].slice(0, 8));
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured()) return;
    let active = true;
    onForegroundMessage((payload) => {
      if (!active) return;
      const data = (payload.data ?? {}) as Record<string, string>;
      if (data.module && data.module !== 'rides') return;
      if (filter?.bookingId && data.bookingId && Number(data.bookingId) !== filter.bookingId) return;
      if (filter?.availabilityId && data.availabilityId && Number(data.availabilityId) !== filter.availabilityId) return;
      const title = payload.notification?.title ?? data.title ?? 'Ride update';
      const body = payload.notification?.body ?? data.body ?? '';
      const kind: PushCardKind = /cancel/i.test(title) ? 'bad' : /arriv|confirm|booked/i.test(title) ? 'ok' : 'info';
      push(title, body, kind);
    }).catch(() => { /* messaging unsupported in this browser */ });
    return () => { active = false; };
  }, [filter?.bookingId, filter?.availabilityId, push]);

  return { items, push };
}
